import { useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Upload, FolderUp, X, Loader2, CheckCircle2, AlertTriangle, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { compressImage, parseFilename } from "@/lib/imageCompress";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const SUPPORTED_EXT = ["jpg", "jpeg", "png", "webp"];
const isSupported = (f) => {
  const ext = (f.name.split(".").pop() || "").toLowerCase();
  return SUPPORTED_EXT.includes(ext) || ["image/jpeg", "image/png", "image/webp"].includes(f.type);
};

async function uploadPool(entries, concurrency, onProgress) {
  const results = [];
  let idx = 0;
  let done = 0;
  const worker = async () => {
    while (idx < entries.length) {
      const my = idx++;
      const e = entries[my];
      try {
        const prepared = await compressImage(e.file);
        const fd = new FormData();
        fd.append("file", prepared);
        const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        results[my] = {
          ok: true,
          item: { media_id: data.id, title: e.title, year: e.year ?? null, original_filename: e.file.name, content_type: data.content_type, size: data.size },
        };
      } catch (err) {
        results[my] = { ok: false, name: e.file.name, error: err?.response?.data?.detail || "Upload fehlgeschlagen" };
      } finally {
        done++;
        onProgress(done, e.file.name);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, entries.length) }, worker));
  return results;
}

export default function ArtworkImportDialog({ existingCount, onDone }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("select"); // select | confirm | importing | report
  const [entries, setEntries] = useState([]); // {file, title, status}
  const [dragOver, setDragOver] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });
  const [report, setReport] = useState(null);
  const [errors, setErrors] = useState([]);
  const [rollingBack, setRollingBack] = useState(false);
  const fileRef = useRef();
  const folderRef = useRef();

  const reset = () => {
    setStep("select"); setEntries([]); setConfirmed(false);
    setProgress({ done: 0, total: 0, current: "" }); setReport(null); setErrors([]);
  };

  const addFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []);
    const seen = new Set();
    const next = [];
    for (const f of files) {
      if (!isSupported(f)) { next.push({ file: f, title: "", year: null, status: "unsupported" }); continue; }
      const key = `${f.name}::${f.size}`;
      const status = seen.has(key) ? "duplicate" : "ready";
      if (status === "ready") seen.add(key);
      const { title, year } = parseFilename(f.name);
      next.push({ file: f, title, year, status });
    }
    setEntries(next);
  }, []);

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };

  const ready = entries.filter((e) => e.status === "ready");
  const dupes = entries.filter((e) => e.status === "duplicate");
  const unsupported = entries.filter((e) => e.status === "unsupported");

  const startImport = async () => {
    setStep("importing");
    setProgress({ done: 0, total: ready.length, current: "" });
    const results = await uploadPool(ready, 6, (done, current) => setProgress({ done, total: ready.length, current }));
    const okItems = results.filter((r) => r?.ok).map((r) => r.item);
    const failed = results.filter((r) => r && !r.ok).map((r) => ({ name: r.name, error: r.error }));
    setErrors(failed);
    if (okItems.length === 0) {
      toast.error("Kein Bild konnte hochgeladen werden. Bestehende Kunstwerke bleiben unverändert.");
      setReport({ created: 0, replaced_old: existingCount, media_removed: 0, aborted: true });
      setStep("report");
      return;
    }
    try {
      const { data } = await api.post("/admin/artworks/import/commit", { confirm: true, items: okItems });
      setReport(data);
      setStep("report");
      toast.success(`${data.created} Kunstwerke importiert`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import fehlgeschlagen");
      setReport({ created: 0, replaced_old: existingCount, media_removed: 0, aborted: true });
      setStep("report");
    }
  };

  const doRollback = async () => {
    if (!report?.import_id) return;
    setRollingBack(true);
    try {
      const { data } = await api.post(`/admin/artworks/import/rollback/${report.import_id}`);
      toast.success(`Zurückgesetzt: ${data.restored} frühere Kunstwerke wiederhergestellt`);
      setOpen(false); reset(); onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rollback fehlgeschlagen");
    } finally { setRollingBack(false); }
  };

  const finish = () => { setOpen(false); reset(); onDone?.(); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="open-import-dialog" variant="outline" className="rounded-md border-[#632A8E] text-[#632A8E] hover:bg-[#F4EAFB]">
          <FolderUp className="w-4 h-4 mr-2" /> Kunstwerke neu importieren
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Kunstwerke neu importieren</DialogTitle>
          <DialogDescription>
            Alle bestehenden Kunstwerke durch Bilder aus einem Ordner ersetzen. Titel werden automatisch aus den Dateinamen erzeugt.
          </DialogDescription>
        </DialogHeader>

        {/* STEP 1: Auswahl */}
        {step === "select" && (
          <div className="space-y-5">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              data-testid="import-dropzone"
              className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${dragOver ? "border-[#F4C542] bg-[#FFF9E8]" : "border-[#E7D8C6] bg-[#FBF6EE]"}`}
            >
              <Upload className="w-8 h-8 mx-auto text-[#9A86B4]" strokeWidth={1.5} />
              <p className="mt-3 text-sm text-[#6E5A82]">Bilder oder einen ganzen Ordner hierher ziehen</p>
              <div className="mt-4 flex flex-wrap gap-3 justify-center">
                <Button type="button" size="sm" variant="outline" className="rounded-md" data-testid="pick-files-btn" onClick={() => fileRef.current?.click()}>
                  Bilder auswählen
                </Button>
                <Button type="button" size="sm" variant="outline" className="rounded-md" data-testid="pick-folder-btn" onClick={() => {
                  if (folderRef.current) { folderRef.current.setAttribute("webkitdirectory", ""); folderRef.current.setAttribute("directory", ""); }
                  folderRef.current?.click();
                }}>
                  <FolderUp className="w-4 h-4 mr-1.5" /> Ordner auswählen
                </Button>
              </div>
              <p className="mt-3 text-xs text-[#9A86B4]">Unterstützt: JPG, JPEG, PNG, WEBP</p>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" multiple hidden onChange={(e) => addFiles(e.target.files)} />
              <input ref={folderRef} type="file" multiple hidden onChange={(e) => addFiles(e.target.files)} />
            </div>

            {entries.length > 0 && (
              <>
                <div className="flex flex-wrap gap-2 text-sm">
                  <span className="px-3 py-1 rounded-full bg-[#E9F9EE] text-[#1F7A45]" data-testid="count-ready">{ready.length} gültig</span>
                  {dupes.length > 0 && <span className="px-3 py-1 rounded-full bg-[#FFF4D6] text-[#8A6D00]" data-testid="count-dupes">{dupes.length} Dubletten</span>}
                  {unsupported.length > 0 && <span className="px-3 py-1 rounded-full bg-[#FCE8E6] text-[#B3261E]" data-testid="count-unsupported">{unsupported.length} nicht unterstützt</span>}
                  <span className="px-3 py-1 rounded-full bg-[#EFE7F7] text-[#632A8E]">{entries.length} Dateien gesamt</span>
                </div>

                <div className="border border-[#E7D8C6] rounded-md max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-[#F5EBDB] text-[#6E5A82]">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Datei</th>
                        <th className="text-left px-3 py-2 font-medium">Neuer Titel</th>
                        <th className="text-left px-3 py-2 font-medium">Jahr</th>
                        <th className="text-left px-3 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((e, i) => (
                        <tr key={i} className="border-t border-[#F5EBDB]" data-testid={`import-row-${i}`}>
                          <td className="px-3 py-2 truncate max-w-[200px]">{e.file.name}</td>
                          <td className="px-3 py-2 truncate max-w-[200px]">{e.title || "–"}</td>
                          <td className="px-3 py-2" data-testid={`import-year-${i}`}>{e.year || "–"}</td>
                          <td className="px-3 py-2">
                            {e.status === "ready" && <span className="text-[#1F7A45]">Bereit</span>}
                            {e.status === "duplicate" && <span className="text-[#8A6D00]">Dublette</span>}
                            {e.status === "unsupported" && <span className="text-[#B3261E]">Nicht unterstützt</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <DialogFooter className="gap-2">
                  <Button variant="ghost" className="rounded-md" data-testid="clear-selection" onClick={() => setEntries([])}>
                    <X className="w-4 h-4 mr-1.5" /> Leeren
                  </Button>
                  <Button className="rounded-md bg-[#F4C542] text-[#32104F] hover:bg-[#D85CA4]" disabled={ready.length === 0} data-testid="to-confirm-btn" onClick={() => setStep("confirm")}>
                    Weiter zur Bestätigung
                  </Button>
                </DialogFooter>
              </>
            )}
          </div>
        )}

        {/* STEP 2: Bestätigung */}
        {step === "confirm" && (
          <div className="space-y-5">
            <div className="rounded-md bg-[#FCE8E6] border border-[#F1B0AB] p-4 flex gap-3">
              <AlertTriangle className="w-6 h-6 text-[#B3261E] shrink-0" />
              <div className="text-sm text-[#7A1D17]">
                <p className="font-medium">Alle bestehenden Kunstwerke werden ersetzt.</p>
                <p className="mt-1">Diese Aktion kann nicht einfach rückgängig gemacht werden (ein Rollback ist direkt nach dem Import möglich).</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="rounded-md border border-[#E7D8C6] p-4" data-testid="existing-count">
                <div className="text-3xl font-serif">{existingCount}</div>
                <div className="text-xs text-[#9A86B4] mt-1">Bestehende Kunstwerke</div>
              </div>
              <div className="rounded-md border border-[#E7D8C6] p-4" data-testid="new-count">
                <div className="text-3xl font-serif">{ready.length}</div>
                <div className="text-xs text-[#9A86B4] mt-1">Neue Bilder</div>
              </div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} data-testid="confirm-replace-checkbox" className="mt-0.5" />
              <span className="text-sm text-[#4A2E68]">Ich bestätige, dass alle bestehenden Kunstwerke ersetzt werden sollen.</span>
            </label>
            <DialogFooter className="gap-2">
              <Button variant="ghost" className="rounded-md" onClick={() => setStep("select")}>Zurück</Button>
              <Button className="rounded-md bg-destructive text-white hover:opacity-90" disabled={!confirmed} data-testid="start-import-btn" onClick={startImport}>
                Import starten &amp; ersetzen
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* STEP 3: Import läuft */}
        {step === "importing" && (
          <div className="space-y-5 py-4" data-testid="import-progress">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-[#632A8E]" />
              <span className="font-medium">Importiere Kunstwerke …</span>
            </div>
            <Progress value={progress.total ? (progress.done / progress.total) * 100 : 0} className="h-3" />
            <div className="text-sm text-[#6E5A82]">
              {progress.done} / {progress.total} hochgeladen
              {progress.current && <span className="block text-xs text-[#9A86B4] truncate mt-1">Aktuell: {progress.current}</span>}
            </div>
            <p className="text-xs text-[#9A86B4]">Bitte dieses Fenster geöffnet lassen, bis der Import abgeschlossen ist.</p>
          </div>
        )}

        {/* STEP 4: Bericht */}
        {step === "report" && report && (
          <div className="space-y-5" data-testid="import-report">
            <div className="flex items-center gap-3">
              {report.aborted ? <AlertTriangle className="w-7 h-7 text-[#B3261E]" /> : <CheckCircle2 className="w-7 h-7 text-[#1F7A45]" />}
              <div>
                <p className="font-serif text-xl">{report.aborted ? "Import abgebrochen" : "Import abgeschlossen"}</p>
                {!report.aborted && <p className="text-sm text-[#6E5A82]">Die neuen Kunstwerke wurden als Entwurf angelegt.</p>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-md border border-[#E7D8C6] p-3">
                <div className="text-2xl font-serif text-[#1F7A45]">{report.created}</div>
                <div className="text-xs text-[#9A86B4]">Erstellt</div>
              </div>
              <div className="rounded-md border border-[#E7D8C6] p-3">
                <div className="text-2xl font-serif">{report.replaced_old}</div>
                <div className="text-xs text-[#9A86B4]">Ersetzt (alt)</div>
              </div>
              <div className="rounded-md border border-[#E7D8C6] p-3">
                <div className="text-2xl font-serif text-[#B3261E]">{errors.length}</div>
                <div className="text-xs text-[#9A86B4]">Fehler (Upload)</div>
              </div>
            </div>
            {errors.length > 0 && (
              <div className="border border-[#F1B0AB] rounded-md bg-[#FCE8E6] p-3 max-h-40 overflow-y-auto text-sm">
                <p className="font-medium text-[#7A1D17] mb-2">Nicht importierte Dateien:</p>
                <ul className="space-y-1 text-[#7A1D17]">
                  {errors.map((e, i) => <li key={i} data-testid={`import-error-${i}`}>• {e.name} — {e.error}</li>)}
                </ul>
              </div>
            )}
            <DialogFooter className="gap-2">
              {!report.aborted && report.import_id && (
                <Button variant="outline" className="rounded-md" data-testid="rollback-btn" disabled={rollingBack} onClick={doRollback}>
                  {rollingBack ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />} Rückgängig machen
                </Button>
              )}
              <Button className="rounded-md bg-[#F4C542] text-[#32104F] hover:bg-[#D85CA4]" data-testid="show-artworks-btn" onClick={finish}>
                Neue Kunstwerke anzeigen
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
